import React from 'react';
import { Page, View, Text, Image, StyleSheet } from '@react-pdf/renderer';
import { ReportData } from '../../types/report.types';
import { useSectionPages } from '../SectionPageContext';

interface SummaryOfFindingsPageProps {
  reportData: ReportData;
  pieChartImage?: string;
  barChartImage?: string;
  sectionId?: string;
}

const styles = StyleSheet.create({
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 0,
    right: 0,
    fontSize: 10,
    color: 'grey',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 24,
  },
  footerLeft: {
    textAlign: 'left',
    flex: 1,
  },
  footerRight: {
    textAlign: 'right',
    flex: 1,
  },
});

const SummaryOfFindingsPage: React.FC<SummaryOfFindingsPageProps> = ({ reportData, pieChartImage, barChartImage, sectionId }) => {
  const { updateSectionPage } = useSectionPages();
  return (
    <Page size="A4" id={sectionId} style={{
      flexDirection: 'column',
      backgroundColor: '#ffffff',
      padding: '20mm 15mm',
      fontFamily: 'Helvetica',
      fontSize: 10,
    }}>
      <View style={{ flex: 1 }}>
        <Text style={{ fontSize: 20, fontWeight: 'bold', color: '#2563eb', marginBottom: 14 }}>
          SUMMARY OF FINDINGS
        </Text>
        <Text style={{ fontSize: 13, marginBottom: 12, color: '#374151' }}>
          This table provides the summary of the vulnerabilities that were identified during the assessment of  {reportData.program_name}:
        </Text>
        {/* Summary Table - Modern Professional Design */}
        <View style={{
          borderWidth: 1,
          borderColor: '#cbd5e1',
          borderRadius: 8,
          overflow: 'hidden',
          marginBottom: 16,
          backgroundColor: '#ffffff',
          width: '95%',
          alignSelf: 'center',
        }}>
          {/* Table Header */}
          <View style={{
            flexDirection: 'row',
            backgroundColor: '#1e40af',
            borderBottomWidth: 2,
            borderBottomColor: '#1e3a8a',
          }}>
            <Text style={{
              paddingVertical: 10,
              paddingHorizontal: 16,
              fontWeight: 'bold',
              width: '70%',
              color: '#ffffff',
              fontSize: 11,
              letterSpacing: 0.5,
            }}>SEVERITY LEVEL</Text>
            <Text style={{
              paddingVertical: 10,
              paddingHorizontal: 16,
              fontWeight: 'bold',
              width: '30%',
              color: '#ffffff',
              fontSize: 11,
              letterSpacing: 0.5,
              textAlign: 'center',
            }}>COUNT</Text>
          </View>

          {/* Critical Row */}
          <View style={{
            flexDirection: 'row',
            backgroundColor: '#ffffff',
            borderBottomWidth: 1,
            borderBottomColor: '#e2e8f0',
            alignItems: 'center',
          }}>
            <View style={{
              paddingVertical: 8,
              paddingHorizontal: 16,
              width: '70%',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 10,
            }}>
              <View style={{
                width: 8,
                height: 8,
                backgroundColor: '#dc2626',
                borderRadius: 4,
              }} />
              <Text style={{
                fontWeight: '600',
                color: '#1e293b',
                fontSize: 11,
              }}>Critical</Text>
            </View>
            <Text style={{
              paddingVertical: 8,
              paddingHorizontal: 16,
              width: '30%',
              fontWeight: 'bold',
              textAlign: 'center',
              fontSize: 11,
              color: '#1e293b',
            }}>{reportData.open_close_counts_by_severity?.Critical?.Total || 0}</Text>
          </View>

          {/* High Row */}
          <View style={{
            flexDirection: 'row',
            backgroundColor: '#fafafa',
            borderBottomWidth: 1,
            borderBottomColor: '#e2e8f0',
            alignItems: 'center',
          }}>
            <View style={{
              paddingVertical: 8,
              paddingHorizontal: 16,

              width: '70%',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 10,
            }}>
              <View style={{
                width: 8,
                height: 8,
                backgroundColor: '#ea580c',
                borderRadius: 4,
              }} />
              <Text style={{
                fontWeight: '600',
                color: '#1e293b',
                fontSize: 11,
              }}>High</Text>
            </View>
            <Text style={{
              paddingVertical: 8,
              paddingHorizontal: 16,
              width: '30%',
              fontWeight: 'bold',
              textAlign: 'center',
              fontSize: 11,
              color: '#1e293b',
            }}>{reportData.open_close_counts_by_severity?.High?.Total || 0}</Text>
          </View>

          {/* Medium Row */}
          <View style={{
            flexDirection: 'row',
            backgroundColor: '#ffffff',
            borderBottomWidth: 1,
            borderBottomColor: '#e2e8f0',
            alignItems: 'center',
          }}>
            <View style={{
              paddingVertical: 8,
              paddingHorizontal: 16,
              width: '70%',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 10,
            }}>
              <View style={{
                width: 8,
                height: 8,
                backgroundColor: '#eab308',
                borderRadius: 4,
              }} />
              <Text style={{
                fontWeight: '600',
                color: '#1e293b',
                fontSize: 11,
              }}>Medium</Text>
            </View>
            <Text style={{
              paddingVertical: 8,
              paddingHorizontal: 16,
              width: '30%',
              fontWeight: 'bold',
              textAlign: 'center',
              fontSize: 11,
              color: '#1e293b',
            }}>{reportData.open_close_counts_by_severity?.Medium?.Total || 0}</Text>
          </View>

          {/* Low Row */}
          <View style={{
            flexDirection: 'row',
            backgroundColor: '#fafafa',
            borderBottomWidth: 1,
            borderBottomColor: '#e2e8f0',
            alignItems: 'center',
          }}>
            <View style={{
              paddingVertical: 8,
              paddingHorizontal: 16,
              width: '70%',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 10,
            }}>
              <View style={{
                width: 8,
                height: 8,
                backgroundColor: '#22c55e',
                borderRadius: 4,
              }} />
              <Text style={{
                fontWeight: '600',
                color: '#1e293b',
                fontSize: 11,
              }}>Low</Text>
            </View>
            <Text style={{
              paddingVertical: 8,
              paddingHorizontal: 16,
              width: '30%',
              fontWeight: 'bold',
              textAlign: 'center',
              fontSize: 11,
              color: '#1e293b',
            }}>{reportData.open_close_counts_by_severity?.Low?.Total || 0}</Text>
          </View>

          {/* Info Row */}
          <View style={{
            flexDirection: 'row',
            backgroundColor: '#ffffff',
            alignItems: 'center',
          }}>
            <View style={{
              paddingVertical: 8,
              paddingHorizontal: 16,
              width: '70%',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 10,
            }}>
              <View style={{
                width: 8,
                height: 8,
                backgroundColor: '#64748b',
                borderRadius: 4,
              }} />
              <Text style={{
                fontWeight: '600',
                color: '#1e293b',
                fontSize: 11,
              }}>Info</Text>
            </View>
            <Text style={{
              paddingVertical: 8,
              paddingHorizontal: 16,
              width: '30%',
              fontWeight: 'bold',
              textAlign: 'center',
              fontSize: 11,
              color: '#1e293b',
            }}>{reportData.open_close_counts_by_severity?.Info?.Total || 0}</Text>
          </View>

          {/* Total Row - Footer */}
          <View style={{
            flexDirection: 'row',
            backgroundColor: '#f1f5f9',
            borderTopWidth: 2,
            borderTopColor: '#cbd5e1',
            alignItems: 'center',
          }}>
            <Text style={{
              paddingVertical: 10,
              paddingHorizontal: 16,
              width: '70%',
              fontWeight: 'bold',
              color: '#1e40af',
              fontSize: 11,
              letterSpacing: 0.3,
            }}>TOTAL FINDINGS</Text>
            <Text style={{
              paddingVertical: 10,
              paddingHorizontal: 16,
              width: '30%',
              fontWeight: 'bold',
              textAlign: 'center',
              color: '#1e40af',
              fontSize: 12,
            }}>{reportData.total_findings || 0}</Text>
          </View>
        </View>
        <Text style={{ fontSize: 11, color: '#64748b', textAlign: 'center', marginBottom: 14 }}>Table 2: Summary of Findings</Text>
        <Text style={{ fontSize: 13, marginBottom: 12, color: '#374151' }}>
          The following chart illustrates the distribution of vulnerabilities by severity:
        </Text>
        {/* Centered Pie Chart - Modern Design */}
        <View style={{ flexDirection: 'row', justifyContent: 'center', marginBottom: 14 }}>
          <View style={{
            width: '60%',
            borderWidth: 1,
            borderColor: '#cbd5e1',
            borderRadius: 12,
            padding: 18,
            backgroundColor: '#ffffff',
            alignItems: 'center',
          }}>
            <Text style={{
              fontSize: 13,
              fontWeight: 'bold',
              textAlign: 'center',
              marginBottom: 12,
              color: '#1e40af',
              letterSpacing: 0.3,
            }}>
              VULNERABILITY DISTRIBUTION BY SEVERITY
            </Text>
            {pieChartImage ? (
              <Image src={pieChartImage} style={{ width: 175, height: 175, objectFit: 'contain', marginBottom: 8 }} />
            ) : (
              <View style={{ width: 175, height: 175, backgroundColor: '#f1f5f9', alignItems: 'center', justifyContent: 'center', borderRadius: 87.5 }}>
                <Text style={{ color: '#64748b', fontSize: 12 }}>No Chart Available</Text>
              </View>
            )}
          </View>
        </View>
        {/*
        // Bar chart commented out as per request
        <View style={{ width: '48%', borderWidth: 1, borderColor: '#e5e7eb', borderRadius: 8, padding: 20, backgroundColor: '#f9fafb', alignItems: 'center' }}>
          <Text style={{ fontSize: 14, fontWeight: 'bold', textAlign: 'center', marginBottom: 8 }}>
            Vulnerability Status
          </Text>
          {barChartImage ? (
            <Image src={barChartImage} style={{ width: 160, height: 160, objectFit: 'contain', marginBottom: 8 }} />
          ) : (
            <View style={{ width: 160, height: 160, backgroundColor: '#e5e7eb', alignItems: 'center', justifyContent: 'center', borderRadius: 12 }}>
              <Text style={{ color: '#64748b', fontSize: 12 }}>No Chart</Text>
            </View>
          )}
        </View>
        */}
        <Text style={{ fontSize: 11, color: '#64748b', textAlign: 'center', marginTop: 8 }}>
          Figure 1: Visualization of Vulnerabilities by Severity and Status
        </Text>
        <Text style={{ display: 'none' }} render={({ pageNumber }) => ''} fixed />
      </View>
      <View style={styles.footer} fixed>
        <Text style={styles.footerLeft}>{reportData.document_number || 'Document Number'}</Text>
        <Text style={styles.footerRight} render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} />
      </View>
    </Page>
  );
};

export default SummaryOfFindingsPage; 